//
// Copyright © 2020 Anticrm Platform Contributors.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import { Analytics } from '@hcengineering/analytics'
import '@hcengineering/platform-rig/profiles/ui/svelte'
import { derived, get, writable } from 'svelte/store'
import { ThemeVariant, type ThemeVariantType } from './variants'

export { default as Theme } from './Theme.svelte'
export { default as InvertedTheme } from './InvertedTheme.svelte'
export { ThemeVariant, type ThemeVariantType } from './variants'

export type ThemeMode = 'light' | 'dark' | 'system'

export interface ThemePreview {
  tint: string
  base: string
  accent: string
  text: string
}

export interface ThemeOption {
  id: string
  label: string
  mode: ThemeMode
  preview: ThemePreview
  colors?: CustomThemeColors
}

export const CUSTOM_THEME_ID = 'theme-custom'

export interface CustomThemeColors {
  sidebar: string
  background: string
  surface: string
  accent: string
  onAccentText: string
  text: string
  mutedText: string
}

const customThemeStorageKey = 'customThemeColors'
const customThemeCssVariables: Record<keyof CustomThemeColors, string> = {
  sidebar: '--custom-theme-sidebar-color',
  background: '--custom-theme-background-color',
  surface: '--custom-theme-surface-color',
  accent: '--custom-theme-accent-color',
  onAccentText: '--custom-theme-on-accent-text-color',
  text: '--custom-theme-text-color',
  mutedText: '--custom-theme-muted-text-color'
}

const getLocalStorage = (): Storage | undefined => {
  if (typeof globalThis === 'undefined') {
    return undefined
  }

  try {
    return globalThis.localStorage
  } catch {
    return undefined
  }
}

const getLocalStorageItem = (key: string): string | null => {
  const storage = getLocalStorage()
  if (storage === undefined) {
    return null
  }

  try {
    return storage.getItem(key)
  } catch {
    return null
  }
}

const setLocalStorageItem = (key: string, value: string): void => {
  const storage = getLocalStorage()
  if (storage === undefined) {
    return
  }

  try {
    storage.setItem(key, value)
  } catch {
    // Keep the live theme update working when browser storage is unavailable.
  }
}

const removeLocalStorageItem = (key: string): void => {
  const storage = getLocalStorage()
  if (storage === undefined) {
    return
  }

  try {
    storage.removeItem(key)
  } catch {
    // Keep the live theme update working when browser storage is unavailable.
  }
}

export const defaultCustomThemeColors: CustomThemeColors = {
  sidebar: '#2A4228',
  background: '#0D1114',
  surface: '#1D272C',
  accent: '#52A545',
  onAccentText: '#E4E6E3',
  text: '#E4E6E3',
  mutedText: '#AAAFA7'
}

const normalizeHexColor = (value: unknown, fallback: string): string => {
  if (typeof value !== 'string') {
    return fallback
  }

  const trimmed = value.trim()
  if (/^#[0-9a-f]{6}$/i.test(trimmed)) {
    return trimmed.toUpperCase()
  }
  if (/^#[0-9a-f]{3}$/i.test(trimmed)) {
    const [, r, g, b] = trimmed
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase()
  }

  return fallback
}

export const normalizeCustomThemeColors = (colors: Partial<CustomThemeColors> = {}): CustomThemeColors => ({
  sidebar: normalizeHexColor(colors.sidebar, defaultCustomThemeColors.sidebar),
  background: normalizeHexColor(colors.background, defaultCustomThemeColors.background),
  surface: normalizeHexColor(colors.surface, defaultCustomThemeColors.surface),
  accent: normalizeHexColor(colors.accent, defaultCustomThemeColors.accent),
  onAccentText: normalizeHexColor(colors.onAccentText, defaultCustomThemeColors.onAccentText),
  text: normalizeHexColor(colors.text, defaultCustomThemeColors.text),
  mutedText: normalizeHexColor(colors.mutedText, defaultCustomThemeColors.mutedText)
})

export const getCustomThemePreview = (colors: CustomThemeColors = getCurrentCustomThemeColors()): ThemePreview => ({
  tint: colors.sidebar,
  base: colors.background,
  accent: colors.accent,
  text: colors.text
})

const getFallbackThemeColors = (preview: ThemePreview, mode: ThemeMode): CustomThemeColors => {
  const dark = mode === 'dark' || mode === 'system'
  return normalizeCustomThemeColors({
    sidebar: preview.tint,
    background: preview.base,
    surface: dark ? '#1D272C' : '#FFFFFF',
    accent: preview.accent,
    onAccentText: dark ? '#F7FAF8' : '#FFFFFF',
    text: preview.text,
    mutedText: dark ? '#AAAFA7' : '#65717A'
  })
}

export const getCurrentCustomThemeColors = (): CustomThemeColors => {
  const raw = getLocalStorageItem(customThemeStorageKey)
  if (raw === null) {
    return defaultCustomThemeColors
  }

  try {
    return normalizeCustomThemeColors(JSON.parse(raw))
  } catch {
    return defaultCustomThemeColors
  }
}

export const customThemeColorStore = writable<CustomThemeColors>(getCurrentCustomThemeColors())

export const applyCustomThemeColors = (
  colors: CustomThemeColors = get(customThemeColorStore),
  root: HTMLElement | undefined = typeof document === 'undefined' ? undefined : document.documentElement
): void => {
  if (root === undefined) return

  const normalized = normalizeCustomThemeColors(colors)
  for (const [key, variable] of Object.entries(customThemeCssVariables) as Array<[keyof CustomThemeColors, string]>) {
    root.style.setProperty(variable, normalized[key])
  }
}

export const clearCustomThemeColors = (
  root: HTMLElement | undefined = typeof document === 'undefined' ? undefined : document.documentElement
): void => {
  if (root === undefined) return

  for (const variable of Object.values(customThemeCssVariables)) {
    root.style.removeProperty(variable)
  }
}

export const setCurrentCustomThemeColors = (colors: Partial<CustomThemeColors>): CustomThemeColors => {
  const normalized = normalizeCustomThemeColors({
    ...get(customThemeColorStore),
    ...colors
  })
  setLocalStorageItem(customThemeStorageKey, JSON.stringify(normalized))
  customThemeColorStore.set(normalized)
  applyCustomThemeColors(normalized)
  return normalized
}

export const resetCurrentCustomThemeColors = (): CustomThemeColors => {
  removeLocalStorageItem(customThemeStorageKey)
  customThemeColorStore.set(defaultCustomThemeColors)
  applyCustomThemeColors(defaultCustomThemeColors)
  return defaultCustomThemeColors
}

const hulyThemeOptions: ThemeOption[] = [
  {
    id: 'theme-light',
    label: 'Light',
    mode: 'light',
    preview: { tint: '#F8F9FA', base: '#FFFFFF', accent: '#3364E2', text: '#0F121A' },
    colors: {
      sidebar: '#F8F9FA',
      background: '#FFFFFF',
      surface: '#FFFFFF',
      accent: '#3364E2',
      onAccentText: '#FFFFFF',
      text: '#0F121A',
      mutedText: '#65717A'
    }
  },
  {
    id: 'theme-dark',
    label: 'Dark',
    mode: 'dark',
    preview: { tint: '#0E0F10', base: '#1F2328', accent: '#6796FF', text: '#FFFFFF' },
    colors: {
      sidebar: '#0E0F10',
      background: '#0E0F10',
      surface: '#1F2328',
      accent: '#6796FF',
      onAccentText: '#FFFFFF',
      text: '#FFFFFF',
      mutedText: '#AAAFA7'
    }
  },
  {
    id: 'theme-system',
    label: 'System',
    mode: 'system',
    preview: { tint: '#F8F9FA', base: '#0E0F10', accent: '#3364E2', text: '#FFFFFF' },
    colors: {
      sidebar: '#F8F9FA',
      background: '#0E0F10',
      surface: '#1F2328',
      accent: '#3364E2',
      onAccentText: '#FFFFFF',
      text: '#FFFFFF',
      mutedText: '#AAAFA7'
    }
  }
]

const customThemeOption: ThemeOption = {
  id: CUSTOM_THEME_ID,
  label: 'Custom',
  mode: 'dark',
  preview: getCustomThemePreview(),
  colors: getCurrentCustomThemeColors()
}

customThemeColorStore.subscribe((colors) => {
  customThemeOption.preview = getCustomThemePreview(colors)
  customThemeOption.colors = colors
})

const pumblePaletteOptions: Array<{ id: string, label: string, tint: string, base: string, accent: string }> = [
  { id: 'wednesday', label: 'Wednesday', tint: '#EEF3F5', base: '#12191D', accent: '#879EAA' },
  { id: 'spellbound', label: 'Spellbound', tint: '#FCDDDD', base: '#4B1707', accent: '#BC3535' },
  { id: 'chaise-lounge', label: 'Chaise Lounge', tint: '#F4E5EC', base: '#431C2E', accent: '#A54572' },
  { id: 'eggplant-garden', label: 'Eggplant Garden', tint: '#F0E5F4', base: '#3A1D44', accent: '#8C45A5' },
  { id: 'man-and-the-sea', label: 'Man and the Sea', tint: '#E5EDF4', base: '#1D3244', accent: '#457AA5' },
  { id: 'crystal-cave', label: 'Crystal Cave', tint: '#E5F1F4', base: '#1D3D44', accent: '#4593A5' },
  { id: 'pickle-jar', label: 'Pickle Jar', tint: '#F1F6DE', base: '#404B14', accent: '#8AA030' },
  { id: 'evergreen', label: 'Evergreen', tint: '#E7F4E5', base: '#273A25', accent: '#52A545' },
  { id: 'martian', label: 'Martian', tint: '#FCEBD1', base: '#452C24', accent: '#A2581B' },
  { id: 'classic', label: 'Classic', tint: '#E9EEF1', base: '#12191D', accent: '#879EAA' }
]

const paletteThemeOptions: ThemeOption[] = pumblePaletteOptions.flatMap((palette) => [
  {
    id: `theme-${palette.id}-light`,
    label: `${palette.label} Light`,
    mode: 'light',
    preview: { tint: palette.tint, base: '#FFFFFF', accent: palette.accent, text: '#11181C' },
    colors: {
      sidebar: palette.tint,
      background: '#FFFFFF',
      surface: '#FFFFFF',
      accent: palette.accent,
      onAccentText: '#FFFFFF',
      text: '#11181C',
      mutedText: '#65717A'
    }
  },
  {
    id: `theme-${palette.id}-dark`,
    label: `${palette.label} Dark`,
    mode: 'dark',
    preview: { tint: palette.base, base: '#182125', accent: palette.accent, text: '#F7FAF8' },
    colors: {
      sidebar: palette.base,
      background: '#12191D',
      surface: '#1D272C',
      accent: palette.accent,
      onAccentText: '#F7FAF8',
      text: '#F7FAF8',
      mutedText: '#AAAFA7'
    }
  }
])

const evergreenDarkColors: CustomThemeColors = {
  sidebar: '#2A4228',
  background: '#0D1114',
  surface: '#1D272C',
  accent: '#52A545',
  onAccentText: '#E4E6E3',
  text: '#E4E6E3',
  mutedText: '#AAAFA7'
}

const evergreenDarkOption = paletteThemeOptions.find((option) => option.id === 'theme-evergreen-dark')
if (evergreenDarkOption !== undefined) {
  evergreenDarkOption.colors = evergreenDarkColors
  evergreenDarkOption.preview = {
    tint: evergreenDarkColors.sidebar,
    base: evergreenDarkColors.background,
    accent: evergreenDarkColors.accent,
    text: evergreenDarkColors.text
  }
}

export const themeOptions: ThemeOption[] = [...hulyThemeOptions, ...paletteThemeOptions, customThemeOption]

export const getThemeOption = (theme: string): ThemeOption | undefined => {
  const option = themeOptions.find((option) => option.id === theme)
  if (option?.id === CUSTOM_THEME_ID) {
    return {
      ...option,
      preview: getCustomThemePreview(get(customThemeColorStore)),
      colors: get(customThemeColorStore)
    }
  }
  return option
}

export const getThemeCustomColors = (theme: string): CustomThemeColors => {
  const option = getThemeOption(theme)
  if (option === undefined) {
    return defaultCustomThemeColors
  }
  if (option.id === CUSTOM_THEME_ID) {
    return get(customThemeColorStore)
  }
  return normalizeCustomThemeColors(option.colors ?? getFallbackThemeColors(option.preview, option.mode))
}

/**
 * @public
 */
export const setDefaultLanguage = (language: string): void => {
  if (getLocalStorageItem('lang') === null) {
    setLocalStorageItem('lang', language)
  }
}

function getDefaultProps (prop: string, value: string): string {
  setLocalStorageItem(prop, value)
  return value
}

/**
 * @public
 */
export const isSystemThemeDark = (): boolean => window.matchMedia('(prefers-color-scheme: dark)').matches
/**
 * @public
 */
export const isThemeDark = (theme: string): boolean => {
  const option = getThemeOption(theme)
  if (option?.mode === 'dark') {
    return true
  }
  if (option?.mode === 'light') {
    return false
  }
  return theme === 'theme-system' && isSystemThemeDark()
}
/**
 * @public
 */
export const getCurrentTheme = (): string => getLocalStorageItem('theme') ?? getDefaultProps('theme', 'theme-system')
/**
 * @public
 */
export const getCurrentFontSize = (): string =>
  getLocalStorageItem('fontsize') ?? getDefaultProps('fontsize', 'normal-font')
/**
 * @public
 */
export const getCurrentLanguage = (): string => {
  const lang = getLocalStorageItem('lang') ?? getDefaultProps('lang', 'en')
  Analytics.setTag('language', lang)
  return lang
}
/**
 * @public
 */
export const getCurrentEmoji = (): string => getLocalStorageItem('emoji') ?? getDefaultProps('emoji', 'emoji-system')

export class ThemeOptions {
  readonly variant: ThemeVariantType
  constructor (
    readonly fontSize: number,
    readonly dark: boolean,
    readonly language: string,
    readonly emoji: string
  ) {
    this.variant = dark ? ThemeVariant.Dark : ThemeVariant.Light
  }
}
export const themeStore = writable<ThemeOptions>()

export function initThemeStore (): void {
  themeStore.set(
    new ThemeOptions(
      getCurrentFontSize() === 'normal-font' ? 16 : 14,
      isThemeDark(getCurrentTheme()),
      getCurrentLanguage(),
      getCurrentEmoji()
    )
  )
}

export const languageStore = derived(themeStore, ($theme) => $theme?.language ?? '')
